<template>
  <div>
    <v-progress-linear v-if="cargando" indeterminate color="purple" class="mb-2" />

    <ag-grid-vue
      :gridOptions="gridOptions"
      class="ag-theme-balham ag-grid-listado-detalles-solicitudes-pagos"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      @grid-ready="onGridReady"
      :rowData="rowData"
      :rowSelection="rowSelection"
      colResizeDefault="shift"
      :animateRows="false"
      :floatingFilter="true"
      :pagination="false"
      :rowClassRules="rowClassRules"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
      :alwaysShowVerticalScroll="true"
      :suppressPaginationPanel="true"
      :isRowSelectable="isRowSelectable">
    </ag-grid-vue>
  </div>
</template>
  
<script>
import listadoAgGridMixin from '../mixins/listado-ag-grid.mixin';
import { cellRendererNumeroRegistro, cellRendererSiONo } from '../utils/funciones';
import listadoCuotaAnteriorMixin from '../mixins/listado-cuota-anterior.mixin';
import Boton3HistorialEstadosAcademicos from './Boton3HistorialEstadosAcademicos.vue';

export default {
  name: 'ListadoDetallesSolicitudesPagos',
  mixins: [listadoAgGridMixin, listadoCuotaAnteriorMixin],
  components: {
    // eslint-disable-next-line vue/no-unused-components
    Boton3HistorialEstadosAcademicos,
  },
  props: {
    rowSelection: {
      type: String,
      default: 'single',
      validator: (value) => ['single', 'multiple'].includes(value),
    },
    itemsYaSeleccionados: {
      type: Array,
      default: () => [],
    },
    sonPagosUniversidad: {
      type: Boolean,
      default: false,
    },
    universidadId: {
      type: [Number, null],
      default: null,
    },
  },
  created() {
    const universidadId = this.universidadId;

    this.obtenerConfiguraciones(universidadId).then(() => {
      this.isRowSelectable = (params) => {
        return (
          !!params.data &&
          !this.itemsYaSeleccionados.some((item) => item.detalleSolicitudPagoId === params.data.id) &&
          !params.data.detallePagoUniversidad &&
          (!this.configuraciones?.verificarPagosCuotaAnterior || params.data.estadoCuotaAnterior !== 'no pagada')
        );
      };
    });

    if (this.sonPagosUniversidad) {
      this.agregarColumnaEstaPagado(5);

      if (this.mostrarColumnaCuotaAnterior) {
        this.agregarColumnaEstadoCuotaAnterior(6);
      }

      this.gridApi?.refreshCells({ force: true });
    }
  },
  computed: {
    rowData() {
      return this.items.map((item) => {
        return {
          ...item,
          estadoCuotaAnterior:
            this.detallePagoConceptoUniversidadCuotaAnterior == null
              ? 'sin cuota anterior'
              : this.inscripcionesProgramasUniversidadesPagadasDeCuotaAnterior.some(
                  (inscripcionPrograma) => inscripcionPrograma.id === item.inscripcionProgramaUniversidadId,
                )
              ? 'pagada'
              : 'no pagada',
        };
      });
    },
  },
  data() {
    return {
      isRowSelectable: null,
      configuraciones: null,
      columnDefs: [
        {
          headerName: 'N° REGISTRO',
          cellRenderer: cellRendererNumeroRegistro,
          filter: false,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          minWidth: 130,
          maxWidth: 130,
        },
        {
          headerName: 'N° DOCUMENTO',
          field: 'inscripcionProgramaUniversidad.inscripcion.estudiante.numeroDocumento',
          minWidth: 120,
          maxWidth: 120,
        },
        {
          headerName: 'PRIMER APELLIDO',
          field: 'inscripcionProgramaUniversidad.inscripcion.estudiante.primerApellido',
          minWidth: 180,
        },
        {
          headerName: 'SEGUNDO APELLIDO',
          field: 'inscripcionProgramaUniversidad.inscripcion.estudiante.segundoApellido',
          minWidth: 180,
        },
        {
          headerName: 'NOMBRES',
          field: 'inscripcionProgramaUniversidad.inscripcion.estudiante.nombres',
          minWidth: 200,
        },
        {
          headerName: 'ESTADO ACADÉMICO',
          cellRenderer: 'Boton3HistorialEstadosAcademicos',
          valueGetter: (params) => {
            const estadoAcademico = params?.data?.inscripcionProgramaUniversidad?.inscripcion?.estadoAcademico;

            return estadoAcademico;
          },
          filter: true,
          minWidth: 250,
          maxWidth: 250,
        },
        {
          headerName: 'PROGRAMA',
          field: 'inscripcionProgramaUniversidad.inscripcion.programa.nombreCompuesto',
          minWidth: 400,
        },
        {
          headerName: 'SEDE',
          field: 'inscripcionProgramaUniversidad.inscripcion.programa.sede.nombre',
          minWidth: 160,
          maxWidth: 160,
        },
        {
          headerName: 'GÉNERO',
          cellRenderer: (params) => {
            const genero = params?.data?.inscripcionProgramaUniversidad?.inscripcion?.estudiante?.sexo;

            return genero === 'M' ? 'MASCULINO' : 'FEMENINO';
          },
          filter: false,
          minWidth: 100,
          maxWidth: 100,
        },
        {
          headerName: 'CELULAR',
          cellRenderer: (params) => {
            const estudiante = params?.data?.inscripcionProgramaUniversidad?.inscripcion?.estudiante;
            const celular = estudiante?.telefono1 || estudiante?.telefono2;

            return celular || '-';
          },
          minWidth: 120,
          maxWidth: 120,
        },
        {
          headerName: 'CORREO',
          cellRenderer: (params) => {
            const correo = params?.data?.inscripcionProgramaUniversidad?.inscripcion?.estudiante?.email;

            return correo || '-';
          },
          minWidth: 250,
        },
        {
          headerName: 'PROFESIÓN',
          cellRenderer: (params) => {
            const profesiones = params?.data?.inscripcionProgramaUniversidad?.inscripcion?.estudiante?.profesiones;

            return profesiones?.length ? profesiones[0].nombre : '-';
          },
          minWidth: 250,
        },
        {
          headerName: 'UNIV. TITULACIÓN',
          cellRenderer: (params) => {
            const universidad =
              params?.data?.inscripcionProgramaUniversidad?.inscripcion?.estudiante?.institucion?.nombre;

            return universidad || '-';
          },
          minWidth: 450,
        },
        {
          headerName: 'CREADO EN',
          field: 'creadoEn',
          filter: false,
          minWidth: 180,
          maxWidth: 180,
        },
      ],
    };
  },
  methods: {
    onSelectionChanged() {
      this.selectedRows = this.gridApi.getSelectedRows();

      if (this.rowSelection === 'single') {
        this.$emit('selectItem', this.selectedRows.length > 0 ? this.selectedRows[0] : null);
      } else {
        this.$emit('selectItem', this.selectedRows);
      }
    },
    agregarColumnaEstaPagado(indiceColumna) {
      this.columnDefs.splice(indiceColumna, 0, {
        headerName: 'ESTA PAGADO',
        cellRenderer: cellRendererSiONo('detallePagoUniversidad', 'PAGADO', 'NO PAGADO'),
        filter: false,
        minWidth: 130,
        maxWidth: 130,
      });
    },
  },
};
</script>
  
<style>
@import '~ag-grid-community/dist/styles/ag-grid.css';
@import '~ag-grid-community/dist/styles/ag-theme-balham.css';

.ag-grid-listado-detalles-solicitudes-pagos {
  height: calc(var(--vh, 1vh) * 100 - 23rem);
}
</style>
  